import { yieldToMainThread } from '@theme/utilities';
import { Component } from '@theme/component';

/**
 * FlyToCart custom element for animating product images to cart
 * This component creates a visual effect of a product "flying" to the cart when added
 */
class FlyToCart extends Component {
  /** @type {Element} */
  source;

  /** @type {boolean} */
  useSourceSize = false;

  /** @type {Element} */
  destination;

  connectedCallback() {
    super.connectedCallback();

    if (!(this.source instanceof Element) || !(this.destination instanceof Element)) {
      this.remove();
      return;
    }

    this.#animate(this.source.getBoundingClientRect(), this.destination.getBoundingClientRect());
  }

  /**
   * Animates the flying thingy along the bezier curve.
   * @param {DOMRectReadOnly} sourceRect - The bounding client rect of the source.
   * @param {DOMRectReadOnly} destinationRect - The bounding client rect of the destination.
   */
  #animate = async (sourceRect, destinationRect) => {
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2,
      y: sourceRect.top + sourceRect.height / 2,
    };

    const pad = 20;
    const endPoint = {
      x: Math.min(Math.max(destinationRect.left + destinationRect.width / 2, pad), window.innerWidth - pad),
      y: Math.min(Math.max(destinationRect.top + destinationRect.height / 2, pad), window.innerHeight - pad),
    };

    if (this.useSourceSize) {
      this.style.setProperty('--width', `${sourceRect.width}px`);
      this.style.setProperty('--height', `${sourceRect.height}px`);
    }
    this.style.setProperty('--start-x', `${startPoint.x}px`);
    this.style.setProperty('--start-y', `${startPoint.y}px`);
    this.style.setProperty('--travel-x', `${endPoint.x - startPoint.x}px`);
    this.style.setProperty('--travel-y', `${endPoint.y - startPoint.y}px`);

    // Start the CSS travel only after the path is set, otherwise it flashes at 0,0.
    this.classList.add('is-ready');

    await yieldToMainThread();

    const animations = this.getAnimations();
    if (animations.length) {
      await Promise.allSettled(animations.map((animation) => animation.finished));
    }

    this.remove();
  };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
